import { useEffect } from "react";
import { Marker, Popup, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { Bed, Bath, Maximize, ArrowRight, MapPin } from "lucide-react";
import { fileUrl } from "../lib/api";
import { Pill } from "./kit";
import { money, titleCase } from "../lib/constants";

export const STATUS_COLOR = { active: "#10b981", for_sale: "#10b981", fsbo: "#D4AF37", pending: "#0ea5e9", under_contract: "#8b5cf6", sold: "#64748b", expired: "#94a3b8", withdrawn: "#94a3b8", off_market: "#94a3b8", preforeclosure: "#f97316", foreclosure: "#ef4444", bankruptcy: "#a855f7" };

export const priceOf = (p) => p.asking_price || p.estimated_value || 0;
export const shortMoney = (v) => (!v ? "—" : v >= 1e6 ? `$${(v / 1e6).toFixed(v % 1e6 === 0 ? 0 : 2)}M` : `$${Math.round(v / 1000)}K`);
export const coverOf = (p) => (p.cover_file_id ? fileUrl(p.cover_file_id) : p.cover_image_url || null);

export function priceIcon(p, active) {
  const approx = p.geocode_approximate;
  const bg = active ? "#D4AF37" : p.is_test ? "#f59e0b" : STATUS_COLOR[p.status] || "#0B1F3A";
  const price = priceOf(p);
  const label = price ? `${approx ? "~" : ""}${shortMoney(price)}` : titleCase(p.distress_type || p.status || "No price");
  const html = `<div class="akn-pin ${approx ? "akn-pin-approx" : ""} ${active ? "akn-pin-active" : ""}" style="background:${bg}">${label}</div>`;
  return L.divIcon({ html, className: "akn-pin-wrap", iconSize: [1, 1], iconAnchor: [0, 0] });
}

export function clusterIcon(cluster) {
  const n = cluster.getChildCount();
  const size = n >= 50 ? 56 : n >= 10 ? 48 : 40;
  const html = `<div class="akn-cluster" style="width:${size}px;height:${size}px"><span>${n}</span><small>homes</small></div>`;
  return L.divIcon({ html, className: "akn-cluster-wrap", iconSize: [size, size], iconAnchor: [size / 2, size / 2] });
}

export function FitBounds({ pts, enabled }) {
  const map = useMap();
  useEffect(() => {
    if (!enabled || !pts.length) return;
    const b = L.latLngBounds(pts.map((p) => [p.latitude, p.longitude]));
    map.fitBounds(b.pad(0.15), { maxZoom: 15, animate: true });
  }, [pts, enabled, map]);
  return null;
}

export function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => { if (target) map.flyTo([target.latitude, target.longitude], Math.max(map.getZoom(), 15), { duration: 0.6 }); }, [target, map]);
  return null;
}

export function MapEvents({ onMoveEnd }) {
  useMapEvents({ moveend: (e) => { const b = e.target.getBounds(); onMoveEnd(`${b.getWest()},${b.getSouth()},${b.getEast()},${b.getNorth()}`); } });
  return null;
}

export function PropertyMarker({ p, active, onHover, onOpen }) {
  return (
    <Marker position={[p.latitude, p.longitude]} icon={priceIcon(p, active)} zIndexOffset={active ? 1000 : 0}
      eventHandlers={{ mouseover: () => onHover(p.id), mouseout: () => onHover(null) }}>
      <Popup maxWidth={280}>
        <PopupCard p={p} onOpen={onOpen} />
      </Popup>
    </Marker>
  );
}

export function PopupCard({ p, onOpen }) {
  const img = coverOf(p);
  return (
    <div className="w-[250px]" data-testid={`map-card-${p.id}`}>
      {img && <img src={img} alt="" className="w-full h-28 object-cover rounded-md mb-2" loading="lazy" />}
      <div className="flex items-center gap-1 flex-wrap mb-1">
        {p.is_test && <span className="bg-amber-400 text-navy-900 text-[9px] font-extrabold px-1 rounded">TEST DATA</span>}
        <Pill className="text-[10px]">{titleCase(p.status || "unknown")}</Pill>
        {p.distress_type && <Pill className="text-[10px] bg-orange-50 text-orange-700 border-orange-200">{titleCase(p.distress_type)}</Pill>}
        {p.geocode_approximate && <Pill className="text-[10px] bg-slate-100 text-slate-500">Approx. location</Pill>}
      </div>
      <p className="font-bold text-navy-800">{p.asking_price ? money(p.asking_price) : p.estimated_value ? `EMV ${money(p.estimated_value)}` : "—"}</p>
      <p className="font-semibold text-navy-800 text-sm leading-tight">{p.address}</p>
      <p className="text-xs text-slate-500">{[p.city, p.state, p.zip].filter(Boolean).join(", ")}</p>
      <Facts p={p} />
      <button onClick={() => onOpen(p.id)} className="mt-2 text-xs font-semibold text-navy-800 flex items-center gap-1 hover:text-gold-dark" data-testid={`map-open-${p.id}`}>Open property <ArrowRight className="w-3 h-3" /></button>
    </div>
  );
}

function Facts({ p }) {
  return (
    <div className="flex gap-3 text-xs text-slate-600 mt-1">
      <span className="flex items-center gap-1"><Bed className="w-3 h-3" />{p.beds || 0} bd</span>
      <span className="flex items-center gap-1"><Bath className="w-3 h-3" />{p.baths || 0} ba</span>
      <span className="flex items-center gap-1"><Maximize className="w-3 h-3" />{p.square_feet ? `${Number(p.square_feet).toLocaleString()} sqft` : "—"}</span>
      {p.days_on_market > 0 && <span>{p.days_on_market} DOM</span>}
    </div>
  );
}

export function ListCard({ p, active, onHover, onSelect, onOpen }) {
  const img = coverOf(p);
  return (
    <div onMouseEnter={() => onHover(p.id)} onMouseLeave={() => onHover(null)} onClick={() => onSelect(p)} data-testid={`map-list-card-${p.id}`}
      className={`flex gap-3 p-2.5 rounded-xl border bg-white cursor-pointer transition-[border-color,box-shadow] ${active ? "border-gold shadow-md" : "border-slate-200 hover:border-slate-300"}`}>
      <div className="w-28 h-24 rounded-lg bg-slate-100 overflow-hidden shrink-0 relative">
        {img ? <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center text-slate-300"><MapPin className="w-6 h-6" /></div>}
        {p.is_test && <span className="absolute top-1 left-1 bg-amber-400 text-navy-900 text-[9px] font-extrabold px-1 rounded">TEST</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="font-display font-bold text-navy-800">{p.asking_price ? money(p.asking_price) : p.estimated_value ? `EMV ${money(p.estimated_value)}` : "—"}</p>
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: STATUS_COLOR[p.status] || "#0B1F3A" }} title={titleCase(p.status || "")} />
        </div>
        <Facts p={p} />
        <p className="text-sm text-navy-800 truncate mt-1">{p.address}</p>
        <p className="text-xs text-slate-500 truncate">{[p.city, p.state, p.zip].filter(Boolean).join(", ")}{p.geocode_approximate ? " · approx. location" : ""}</p>
        <div className="flex items-center justify-between mt-1">
          <span className="text-[11px] text-slate-400">{titleCase(p.status || "")}{p.distress_type && titleCase(p.distress_type) !== titleCase(p.status || "") ? ` · ${titleCase(p.distress_type)}` : ""}</span>
          <button onClick={(e) => { e.stopPropagation(); onOpen(p.id); }} className="text-[11px] font-semibold text-navy-800 hover:text-gold-dark flex items-center gap-0.5" data-testid={`map-list-open-${p.id}`}>Details <ArrowRight className="w-3 h-3" /></button>
        </div>
      </div>
    </div>
  );
}
